import {ReactNode, useState} from 'react';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';

export default function () {
  const {t} = useInferableTranslation();

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_driving_safety_score_guide_eu')}</h1>
      </Header>
      <main className="content page_safe_driving">
        <section className="section">
          <div className="driving_accordion type_faq">
            <ul className="driving_accordion_list">
              <GuideItem
                title={t('msg_dss_driving_distance_criteria_eu')}
                isInitOpen
              >
                <ul className="section_bullet_list body_small_r">
                  <li className="section_bullet_item">{t('msg_dss_based_on_90_days_record_eu')}</li>
                  <li className="section_bullet_item">{t('msg_dss_exclude_records_older_90_days_eu')}</li>
                  <li className="section_bullet_item">{t('msg_dss_distance_may_change_90_days_eu')}</li>
                </ul>
                <div className="accordion_content_imgbox">
                  <div className="text_top">
                    <span className="text_guide01">
                      {t('ctn_dss_excluded_eu')}
                      <span className="blind">D-91</span>
                    </span>
                    <span className="text_guide02">
                      {t('ctn_dss_period_reflected_today_eu')}
                      <span className="blind">D-90, D-89 ... D-1, Today</span>
                    </span>
                  </div>
                  <div className="text_bottom">
                    <span className="text_guide03">
                      {t('ctn_dss_excluded_eu')}
                      <span className="blind">D-91, D-90</span>
                    </span>

                    <span className="text_guide04">
                      {t('ctn_dss_period_reflected_tomorrow_eu')}
                      <span className="blind">D-89 ... D-1, Today, D+1</span>
                    </span>
                  </div>
                  <ImageSelector
                    className="accordion_content_img"
                    alt="accordion_content_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/safe_driving_score/score_distance_guide_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/score_distance_guide.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/safe_driving_score/hyundai/score_distance_guide_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/hyundai/score_distance_guide.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/safe_driving_score/genesis/score_distance_guide_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/genesis/score_distance_guide.png')}
                  />
                </div>
              </GuideItem>
              <GuideItem title={t('msg_dss_what_is_driving_score_eu')}>
                <ul className="section_bullet_list body_small_r">
                  <li className="section_bullet_item">{t('msg_dss_score_calculated_90_days_events_eu')}</li>
                  <li className="section_bullet_item">{t('msg_dss_score_calculated_if_less_90_days_eu')}</li>
                </ul>
                <dl className="accordion_content_define">
                  <div className="define_item">
                    <dt className="define_term">{t('ctn_dss_hard_acceleration_eu')}</dt>
                    <dd className="define_detail">{t('ctn_dss_speed_increase_10km_1sec_eu')}</dd>
                  </div>
                  <div className="define_item">
                    <dt className="define_term">{t('ctn_dss_hard_braking_eu')}</dt>
                    <dd className="define_detail">{t('ctn_dss_speed_decrease_13km_1sec_eu')}</dd>
                  </div>
                  <div className="define_item">
                    <dt className="define_term">{t('ctn_dss_sudden_start_eu')}</dt>
                    <dd className="define_detail">{t('ctn_dss_speed_increase_10km_start_eu')}</dd>
                  </div>
                  <div className="define_item">
                    <dt className="define_term">{t('ctn_dss_late_night_driving_eu')}</dt>
                    <dd className="define_detail">{t('ctn_dss_vehicle_driven_1km_11pm_5am_eu')}</dd>
                  </div>
                </dl>
              </GuideItem>
              <GuideItem title={t('msg_dss_when_score_updated_eu')}>
                <ul className="section_bullet_list body_small_r">
                  <li className="section_bullet_item">{t('msg_dss_score_update_1hr_drive_eu')}</li>
                  <li className="section_bullet_item">{t('msg_dss_update_after_start_2020_eu')}</li>
                </ul>
              </GuideItem>
              <GuideItem title={t('msg_dss_vehicle_vs_score_distance_eu')}>
                <ul className="section_bullet_list body_small_r">
                  <li className="section_bullet_item">{t('msg_dss_vehicle_total_vs_score_90days_eu')}</li>
                </ul>
              </GuideItem>
              <GuideItem title={t('msg_dss_post_drive_distance_diff_eu')}>
                <ul className="section_bullet_list body_small_r">
                  <li className="section_bullet_item">{t('msg_dss_cluster_vs_score_method_eu')}</li>
                </ul>
              </GuideItem>
            </ul>
          </div>
        </section>
      </main>
    </>
  );
}

function GuideItem({title, isInitOpen = false, children}: {title: string; isInitOpen?: boolean; children: ReactNode}) {
  const [isOpen, setIsOpen] = useState(isInitOpen);

  return (
    <li className="driving_accordion_item">
      <button
        type="button"
        className="driving_accordion_btn"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
      >
        <span
          className="question_mark"
          aria-hidden={!isOpen}
        >
          Q
        </span>
        <span
          className="driving_accordion_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {title}
        </span>
        <span
          className="ic ic_arrow_down_16px"
          aria-hidden="true"
        ></span>
      </button>
      <div
        className="driving_accordion_content"
        style={{display: isOpen ? 'block' : 'none'}}
      >
        {children}
      </div>
    </li>
  );
}
